import React, { useCallback, useEffect, useMemo } from 'react';

import { Input, Button, Select } from 'antd';
import Form from '../../components/Form';
import { showNotification, ERROR_TYPE } from '../../components/Notification';
import styles from './index.module.scss';
import { LAYOUT_0_24, VALIDATE_MESSAGES } from '../../constants/form';

import WealthieBreadCrumb from '../../components/BreadCrumb';
import withLoading from '../../helpers/withLoadingHOC';
import { useHistory, useParams } from 'react-router-dom';
import { createAdminApi, getAdminDetailApi, updateAdminApi } from '../../api';
import { get } from 'lodash';
import { emailValidator, passwordValidator } from '../../helpers/validators';

const { Option } = Select;

type CreateUpdateAdminParam = {
  adminId: string;
};

function CreateAdmin(): React.ReactElement {
  const [form] = Form.useForm();
  const history = useHistory();
  // const dispatch = useDispatch();
  const { adminId } = useParams<CreateUpdateAdminParam>();

  const breadcrumbConfig = useMemo(
    () => [
      {
        name: 'Wealthie',
      },
      {
        name: 'Admin users',
        link: '/user-admin',
      },
      {
        name: adminId ? 'Update Admin' : 'Create Admin',
      },
    ],
    [adminId],
  );

  useEffect(() => {
    // fetch initial data, check admin
    async function getCurrentAdmin() {
      try {
        const res = await getAdminDetailApi(adminId);
        form.setFieldsValue(res.data);
      } catch (error) {
        showNotification({
          type: 'error',
          message: 'Can not retrieve data, please try again',
        });
      }
    }
    adminId && getCurrentAdmin();
  }, [adminId, form]);

  const onFinish = useCallback(
    async (values) => {
      try {
        if (adminId) {
          await updateAdminApi(adminId, values);
        } else {
          await createAdminApi(values);
        }

        showNotification({
          type: 'success',
          message: 'Successfully!',
        });
        history.push('/user-admin');
      } catch (error) {
        showNotification({
          type: 'error',
          message: get(
            error,
            'response.data.message',
            'Somethings went wrong, please try again',
          ),
        });
      }
      // todo on finished
    },
    [history, adminId],
  );

  const onFinishFailed = useCallback(() => {
    showNotification({
      type: ERROR_TYPE,
      message: 'Please fill in proper form values.',
    });
  }, []);

  return (
    <>
      <WealthieBreadCrumb breadcrumbConfig={breadcrumbConfig} />
      <div data-testid="edit_profile_page" className={styles.wrapper}>
        <div className={styles.create_admin_wrapper}>
          <Form
            initialValues={{ userType: 'read_only' }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            form={form}
            validateMessages={VALIDATE_MESSAGES}
            {...LAYOUT_0_24}
            data-testid="edit_profile_form"
          >
            <Form.Item
              rules={[{ required: true, validator: emailValidator }]}
              label="Email"
              name="email"
            >
              <Input aria-label="email-input" placeholder="Your Email" />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: !adminId && true,
                  ...(!adminId && { validator: passwordValidator }),
                },
              ]}
              label="Password"
              name="password"
            >
              <Input
                type="password"
                aria-label="password-input"
                placeholder="Your Password"
              />
            </Form.Item>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true }]}
            >
              <Input aria-label="fName-input" placeholder="Your First Name" />
            </Form.Item>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true }]}
            >
              <Input aria-label="lName-input" placeholder="Your Last Name" />
            </Form.Item>

            <Form.Item
              label="User Type"
              name="userType"
              rules={[{ required: false }]}
            >
              <Select className={styles.custom_select} placeholder="User Type">
                <Option value="admin">Admin</Option>
                <Option value="read_only">Read Only</Option>
              </Select>
            </Form.Item>
            <Form.Item className={styles.action_section}>
              <Button
                type="primary"
                htmlType="submit"
                data-testid="save_button"
                className={styles.save_button}
              >
                Save
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </>
  );
}

export default withLoading(CreateAdmin);
